/* eslint-disable import/no-cycle */
/* eslint-disable no-unused-expressions */
import { isMobile } from "react-device-detect";
import thdCustomer from "@thd-olt-global/thd-customer";
import { find, isNil, replace } from "lodash";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import relativeTime from "dayjs/plugin/relativeTime";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { getCookie } from "./cookies";
import { isLLC, setUrl, getUrlParam } from "./urls";
import { isoCountries } from "../constants";
import newRelic from "./newrelic";

const isLocalDev = () => window.location.origin.indexOf("local") !== -1;

const isNativeApp = () => {
  return typeof window === "undefined" ? false : window.__EXPERIENCE_CONTEXT__?.isConsumerApp;
};

const phoneNumberRegex = /^(\d{3})(\d{3})(\d{4})$/g;

// returns the channel type: desktop or mobile. channel is identified using the request header set by Akamai. This is passed down to FED as a FS.
const getChannel = () => (
  isMobile ? "mobile" : "desktop"
);

const getAuthorizationToken = () => window.encodeURIComponent(thdCustomer.token);

const isSsr = typeof window === "undefined";

const getTmxId = () => (isSsr ? "" : window.THD_ACCOUNT_LIB?.TMX?.getTMXsessionId());

function getEnv() {
  return isLLC() ? "LLC" : "PROD";
}

const getTMXSessionID = () => {
  const nativeTMXprofileId = getUrlParam("guid");
  if (isNativeApp() && nativeTMXprofileId) {
    return nativeTMXprofileId;
  }
  return typeof window === "undefined" ? "" : window?.THD_ACCOUNT_LIB?.TMX?.sessionId;
};

const formatPhoneNumber = (phoneNumber) => phoneNumber.replace(phoneNumberRegex, "$1-$2-$3");

const formatPhoneNumber2 = (phoneNumber) => phoneNumber.replace(phoneNumberRegex, "($1) $2-$3");

const maxEmailLength = 5;

const censorWord = (str) => {
  let out = str[0];
  if (str.length > 1) {
    out = out + "*".repeat(Math.min(maxEmailLength, str.length - 2)) + str.slice(-1);
  }
  return out;
};

const censorEmail = (email) => {
  if (email) {
    const arr = email.split("@");
    return `${censorWord(arr[0])}@${censorWord(arr[1])}`;
  }
  return "";
};

const getQuantumMetricUserId = () => getCookie("QuantumMetricUserID");

const getQuantumMetricSessionId = () => getCookie("QuantumMetricSessionID");

const getEmbraceCookie = () => getCookie("MOBILE_DEVICEID") || "";

const getVerifiedPhone = (account) => {
  const phones = account?.profilePhones?.phone || [];
  const verifiedPhone = find(phones, (ph) => ph?.primaryFlag === "Y" && ph?.phoneVerified === "Y");
  return verifiedPhone ? verifiedPhone.number : undefined;
};

const getSvocId = () => (isSsr ? null : window.THDCustomer?.default?.svocID);

const getUserId = () => (isSsr ? null : window.THDCustomer?.default?.userID);

const getRole = () => (isSsr ? null : window.THDCustomer?.default?.role);

const generateXB3TraceID = () => Math.random().toString(16).substring(2)
  + new Date().getTime().toString(16).substring(0, 15);

const formatDeviceInfo = (deviceList) => {
  dayjs.extend(timezone);
  dayjs.extend(advancedFormat);
  dayjs.extend(relativeTime);

  return deviceList.map((device) => {
    const timeFromNow = device.signInTS === 0 ? 0 : dayjs(device.signInTS).fromNow();
    const readableTS = `${dayjs(device.signInTS).format("MMM D YYYY")} at ${dayjs(device.signInTS).format("h:mm a z")}`;
    let finalTimeString = `Signed in ${readableTS}`;
    if (timeFromNow === 0) {
      finalTimeString = "Signed in";
    } else if ((timeFromNow.indexOf("minutes") > -1) || (timeFromNow.indexOf("seconds") > -1)) {
      finalTimeString = `Signed in ${timeFromNow}`;
    }

    if (device.current) { finalTimeString = "Currently Using This Device"; }

    return { ...device,
      formattedTime: finalTimeString,
      deviceCountry: isoCountries[device.deviceCountry] ? isoCountries[device.deviceCountry] : device.deviceCountry,
      deviceOS: !isNil(device.deviceOS) ? replace(device.deviceOS, /_/g, ".") : undefined };
  });
};

const isFeatureSwitchEnabled = (featureSwitchName) => {
  return typeof window !== "undefined" && (getCookie(featureSwitchName)
          || window.__EXPERIENCE_PROPS__?.expConfiguration[featureSwitchName] === "true" // eslint-disable-next-line eqeqeq
          || window.__EXPERIENCE_PROPS__?.expConfiguration[featureSwitchName] == "1");
};

const isUserB2B = () => {
  return thdCustomer.isB2BCustomer;
};

const FEATURE_SWITCHES = {
  ENABLE_B2B_2FA: "ENABLE_B2B_2FA",
  ENABLE_B2B_DEVICE_MGMT: "ENABLE_B2B_DEVICE_MGMT",
  ENABLE_PWDLESS_UNENROLL: "ENABLE_PWDLESS_UNENROLL",
  ENABLE_WEB_AUTHN_UNENROLL: "ENABLE_WEB_AUTHN_UNENROLL",
  ENABLE_NEW_HEADER_STYLES: "ENABLE_NEW_HEADER_STYLES",
  ENABLE_NATIVE_APP_PASSWORDLESS_ENROLLMENT: "ENABLE_NATIVE_APP_PASSWORDLESS_ENROLLMENT",
  ENABLE_WEBAUTHN_ENROLLMENT_SECURITY_PAGE: "ENABLE_WEBAUTHN_ENROLLMENT_SECURITY_PAGE",
  ENABLE_NEW_LEFT_NAVBAR: "ENABLE_NEW_LEFT_NAVBAR",
  ENABLE_WEBAUTHN_UPGRADE_SECURITY_PAGE: "ENABLE_WEBAUTHN_UPGRADE_SECURITY_PAGE",
  ENABLE_B2B_PHONE_VERIFICATION: "ENABLE_B2B_PHONE_VERIFICATION",
  ENABLE_ACCOUNT_ACTIVITY_LOG: "ENABLE_ACCOUNT_ACTIVITY_LOG",
  ENABLE_SET_PASSWORD_IN_SIGNIN_WITH_PASSWORD_INSTEAD: "ENABLE_SET_PASSWORD_IN_SIGNIN_WITH_PASSWORD_INSTEAD",
  ENABLE_NATIVE_WEBAUTHN: "ENABLE_NATIVE_WEBAUTHN",
};

const isB2b2FaEnabled = () => isFeatureSwitchEnabled(FEATURE_SWITCHES.ENABLE_B2B_2FA);

const isAccountActivityLogEnabled = () => {
  return (isFeatureSwitchEnabled(FEATURE_SWITCHES.ENABLE_ACCOUNT_ACTIVITY_LOG));
};

const isPasswordlessEnrollmentForNativeAppOn = () => {
  return (isFeatureSwitchEnabled(FEATURE_SWITCHES.ENABLE_NATIVE_APP_PASSWORDLESS_ENROLLMENT) && isNativeApp());
};

const isWebauthnEnabledForNotNativeAppUsers = () => {
  return (isFeatureSwitchEnabled(FEATURE_SWITCHES.ENABLE_WEBAUTHN_UPGRADE_SECURITY_PAGE) && !isNativeApp());
};

const isWebauthnEnabledForNativeAppUsers = () => {
  return (isFeatureSwitchEnabled(FEATURE_SWITCHES.ENABLE_NATIVE_WEBAUTHN) && isNativeApp());
};

const isWebAuthnEnabled = () => {
  return isWebauthnEnabledForNotNativeAppUsers() || isWebauthnEnabledForNativeAppUsers();
};

const isSetPasswordInSigninWithPasswordInstead = () => {
  return (isFeatureSwitchEnabled(FEATURE_SWITCHES.ENABLE_SET_PASSWORD_IN_SIGNIN_WITH_PASSWORD_INSTEAD));
};

const postDataToEventBus = (eventName, payload) => {
  window?.LIFE_CYCLE_EVENT_BUS?.lifeCycle?.trigger(eventName, payload);
  newRelic.logLifecycleEventSent(eventName, payload);
};

/**
 * Subscribe to an event that may have already happened in the past
 * @param eventName
 * @param callback
 */
const subscribeToLifeCycleEvent = (eventName, callback) => {
  window?.LIFE_CYCLE_EVENT_BUS?.lifeCycle?.on(eventName, ({ output }) => {
    callback(output);
    newRelic.logLifecycleEventReceived(eventName, output);
  });
};

/**
 * Subscribe to a future event
 * @param eventName
 * @param callback
 */
const subscribeToEvent = (eventName, callback) => {
  window?.LIFE_CYCLE_EVENT_BUS?.on(eventName, ({ output }) => {
    callback(output);
    newRelic.logLifecycleEventReceived(eventName, output);
  });
};

const getPrimaryPhone = (account) => {
  const phones = account?.profilePhones?.phone || [];
  let primaryPhone = null;
  phones.forEach((profilePhone) => {
    if (profilePhone.primaryFlag === "Y") primaryPhone = profilePhone.number;
  });
  return primaryPhone;
};

const isWebauthnEnrollmentEnabledForNotNativeAppUsers = () => {
  return (!isNativeApp() && isFeatureSwitchEnabled(FEATURE_SWITCHES.ENABLE_WEBAUTHN_ENROLLMENT_SECURITY_PAGE));
};

function validateResponse(response) {
  if (typeof response === "object" && response !== null) {
    if (response.error) {
      throw new Error("API call failed: " + response.error);
    }
    return response;
  }

  if (typeof response === "string") {
    try {
      return JSON.parse(response);
    } catch (error) {
      throw new Error("Invalid JSON response: " + error.message);
    }
  }

  throw new Error("Invalid response format: " + typeof response);
}

const checkNativeAppVersion = () => {
  const targetVersion = "7.46.2";
  const parseVersion = (v) => {
    return v.split(".").map(Number);
  };

  const appVersion = navigator.userAgent.match(/THDConsumer\/([0-9.]+)/);
  if (!appVersion || !appVersion[1]) return false;

  const current = parseVersion(appVersion[1]);
  const target = parseVersion(targetVersion);

  for (let i = 0; i < target.length; i++) {
    if ((current[i] || 0) > (target[i] || 0)) return true;
    if ((current[i] || 0) < (target[i] || 0)) return false;
  }
  return true; // exact match
};

export {
  getCookie,
  isLLC,
  getAuthorizationToken,
  getTmxId,
  setUrl,
  getChannel,
  isLocalDev,
  getEnv,
  isB2b2FaEnabled,
  getTMXSessionID,
  formatDeviceInfo,
  formatPhoneNumber,
  formatPhoneNumber2,
  censorEmail,
  getQuantumMetricUserId,
  getQuantumMetricSessionId,
  getEmbraceCookie,
  getVerifiedPhone,
  isSsr,
  getSvocId,
  getUserId,
  getRole,
  generateXB3TraceID,
  isFeatureSwitchEnabled,
  isUserB2B,
  isNativeApp,
  FEATURE_SWITCHES,
  postDataToEventBus,
  subscribeToLifeCycleEvent,
  subscribeToEvent,
  isPasswordlessEnrollmentForNativeAppOn,
  isWebauthnEnrollmentEnabledForNotNativeAppUsers,
  isWebauthnEnabledForNotNativeAppUsers,
  isWebauthnEnabledForNativeAppUsers,
  isWebAuthnEnabled,
  getPrimaryPhone,
  isAccountActivityLogEnabled,
  isSetPasswordInSigninWithPasswordInstead,
  validateResponse,
  checkNativeAppVersion
};
